/**
 * Keyed by `InlineHostKey` / `TourContext`, but typed by `string`: the tour
 * content grows those unions (a new inline host, a new context) and a missing
 * sentence must fall back to the generic one, not break the build or say
 * «undefined». Look them up through `armedToast`/`armedWhere`.
 */
type ByKey = Readonly<Record<string, string>>;

/**
 * The runtime's own Persian words: what the tour RUNNER, the setup bar, the
 * struggle detector and the Help entry say around the tours. The tours' step
 * copy and the hub's fixed labels are `tours/copy.ts` (the tour-content
 * stream's file); this module is the runtime's, and `runtime-copy.test.ts`
 * sweeps it with the same rules as the steps (no Latin letters, no ASCII
 * digits, Persian «ی/ک», ZWNJ before «می»/«ها», none of the retired words).
 *
 * Numbers never appear here as digits: a sentence around a count is split so
 * the count renders through `<Num>`.
 */
export const RUNTIME_COPY = {
  toast: {
    /** A route change closed a running tour (once per run). */
    interrupted: 'راهنما نیمه‌کاره موند. از بخش «راهنما» ادامه بدید.',
    /** Armed inline tours, by host. */
    armedInline: {
      'session-form': 'فرم «ثبت جلسه درمان» رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
      'appointment-form': 'فرم «ثبت نوبت» رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
      'session-detail': 'جزئیات یک جلسه درمان رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
    } as ByKey,
    armedInlineFallback: 'فرمش رو باز کنید؛ راهنما همون‌جا شروع می‌شه.',
  },

  /** The slim notice while a context tour waits for its place. */
  armed: {
    lead: (title: string) => `راهنمای «${title}» آماده است.`,
    where: {
      patient: 'پروندهٔ یک بیمار رو باز کنید تا راهنما شروع بشه.',
      'session-form': 'فرم «ثبت جلسه درمان» رو باز کنید تا شروع بشه.',
      'appointment-form': 'فرم «ثبت نوبت» رو باز کنید تا شروع بشه.',
      'import-job':
        'برای بازبینی، یکی از موارد «ثبت اطلاعات از روی عکس» رو باز کنید تا راهنما شروع بشه.',
    } as ByKey,
    whereInline: {
      'session-form': 'فرم «ثبت جلسه درمان» رو باز کنید تا شروع بشه.',
      'appointment-form': 'فرم «ثبت نوبت» رو باز کنید تا شروع بشه.',
      'session-detail': 'جزئیات یک جلسه درمان رو باز کنید تا شروع بشه.',
    } as ByKey,
    whereFallback: 'صفحه یا فرم مربوط به این راهنما رو باز کنید تا شروع بشه.',
    cancel: 'لغو',
    cancelLabel: 'لغو راهنمای آماده',
  },

  /** `SetupStepBar`: a setup step's page, outside «تنظیمات». */
  setup: {
    region: 'راه‌اندازی کلینیک',
    /** «راه‌اندازی: <Num> از <Num>» */
    lead: 'راه‌اندازی:',
    of: 'از',
    thisStep: 'این مرحله:',
    stepDone: 'این مرحله انجام شد.',
    nextStep: (title: string) => `مرحلهٔ بعد: ${title}`,
    allSteps: 'همهٔ مراحل',
  },

  /** The hub's «شروع کار» additions. */
  hub: {
    setupNext: (title: string) => `قدم بعدی: ${title}`,
    setupAll: 'همهٔ مراحل',
    /** «<Num> از <Num> راهنمای پیشنهادی برای نقش شما را دیده‌اید» */
    recommendedTail: 'راهنمای پیشنهادی برای نقش شما',
    recommendedSeen: 'رو دیدید',
    of: 'از',
    /** The patient-file row with no patient to open (a «[why]. [what to do].» reason). */
    noPatients: 'هنوز بیماری ثبت نشده. اول یک بیمار ثبت کنید.',
    noPatientsViewOnly:
      'هنوز بیماری در فهرست شما نیست. وقتی بیماری ثبت شد، از پرونده‌اش شروع کنید.',
    /** «از نو نشان دادن همهٔ راهنماها» while someone else's session is being viewed. */
    resetReadOnlyReason:
      'در حالت ورود به‌جای کاربر، تنظیمات نمایش دوبارهٔ راهنماها تغییر نمی‌کنه. خود کاربر باید از حساب خودش این کار رو انجام بده.',
    autoOfferSwitch: 'پیشنهاد راهنما موقع نیاز',
    autoOfferHelper:
      'وقتی اولین بار یک بخش رو باز می‌کنید یا برای انجام کاری به مشکل می‌خورید، راهنما پیشنهاد می‌دیم. از بخش «راهنما» هم همیشه می‌تونید ببینیدشون.',
  },

  /** A help offer made because the panel noticed someone stuck (critic C6). */
  help: {
    title: 'کمک می‌خواید؟',
    tourBody: 'می‌خواید راهنمای کوتاه این قسمت رو ببینید؟',
    inlineBody: 'می‌خواید راهنمای پر کردن این فرم رو ببینید؟',
    /** When the step the person was stuck on is known. */
    stepBody: (title: string) => `بخش «${title}» رو در راهنمای کوتاه نشونتون می‌دیم.`,
    supportBody:
      'اگر به مشکل خوردید، راهنمای همین صفحه رو ببینید یا با پشتیبانی بوکورا تماس بگیرید.',
    primary: 'نمایش راهنما',
    secondary: 'نه، ممنون',
    notes: 'کمک برای همین صفحه',
    live: 'پیشنهاد کمک آماده است',
  },

  /** The Help menu. */
  menu: {
    pageHelp: 'کمک برای همین صفحه',
    pageHelpStuck: 'کمک برای همین قسمت',
    newTips: 'پیشنهادهای جدید',
  },

  /** The help-notes dialog. */
  notes: {
    fallbackTitle: 'راهنما و پشتیبانی',
    fallbackBody:
      'برای این صفحه راهنمای کوتاهی نداریم. همهٔ راهنماها در «راهنما و پیشنهادها» در دسترس‌اند و پشتیبانی بوکورا هم کمکتون می‌کنه.',
    hub: 'راهنما و پیشنهادها',
    support: 'تماس با پشتیبانی',
    close: 'بستن',
  },

  /** `<ShowMeHow>` under an empty state. */
  showMeHow: 'ببینید چطور کار می‌کنه',
} as const;

/** The toast when an inline tour is armed for `host`. */
export function armedToast(host: string): string {
  return RUNTIME_COPY.toast.armedInline[host] ?? RUNTIME_COPY.toast.armedInlineFallback;
}

/** Where an armed context tour will start: its inline host's form, else its route context. */
export function armedWhere(tour: { inlineHost?: string; route?: { context?: string } }): string {
  if (tour.inlineHost) {
    return RUNTIME_COPY.armed.whereInline[tour.inlineHost] ?? RUNTIME_COPY.armed.whereFallback;
  }
  const context = tour.route?.context;
  if (context) return RUNTIME_COPY.armed.where[context] ?? RUNTIME_COPY.armed.whereFallback;
  return RUNTIME_COPY.armed.whereFallback;
}
